import {
  ICreatorScriptUserInputsPayload,
  useGenerateMasterPrompt,
  useGetAiWorkspace,
  useUpdateScriptGenerationMasterPrompt,
} from "@entities/aiWorkspace";
import { Button, Input, Select } from "@shared/ui";
import { DisabledPopover } from "@shared/ui/DisabledPopover";
import { Wand2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import frameStyles from "./scriptFrame.module.scss";
import styles from "./scriptMasterPrompt.module.scss";
import { promptToText } from "../WorkspacePromptEditor/promptToText";
import {
  DEFAULT_SCRIPT_SETTINGS,
  SCRIPT_DURATIONS,
  SCRIPT_FORMATS,
  SCRIPT_LANGUAGES,
  SCRIPT_TONES,
  matchedScriptOption,
} from "./types";

function savedKey(saved?: ICreatorScriptUserInputsPayload) {
  return [
    saved?.brief,
    saved?.format,
    saved?.duration,
    saved?.toneBalance,
    saved?.language,
  ].join("|");
}

export function ScriptGenerationMasterPromptForm({
  aiWorkspaceId,
  saved,
  showHeading = true,
  framed = true,
}: {
  aiWorkspaceId: string;
  saved?: ICreatorScriptUserInputsPayload;
  showHeading?: boolean;
  framed?: boolean;
}) {
  const { update, isUpdating } = useUpdateScriptGenerationMasterPrompt();
  const [brief, setBrief] = useState(saved?.brief || "");
  const [format, setFormat] = useState(
    matchedScriptOption(saved?.format, SCRIPT_FORMATS, DEFAULT_SCRIPT_SETTINGS.format)
  );
  const [duration, setDuration] = useState(
    matchedScriptOption(
      saved?.duration,
      SCRIPT_DURATIONS.map((item) => item.value),
      DEFAULT_SCRIPT_SETTINGS.duration
    )
  );
  const [tone, setTone] = useState(
    matchedScriptOption(saved?.toneBalance, SCRIPT_TONES, DEFAULT_SCRIPT_SETTINGS.tone)
  );
  const [language, setLanguage] = useState(
    matchedScriptOption(saved?.language, SCRIPT_LANGUAGES, DEFAULT_SCRIPT_SETTINGS.language)
  );
  const { data: workspaceData } = useGetAiWorkspace(aiWorkspaceId);
  const overviewMissing = !promptToText(workspaceData?.data?.overview || "").trim();
  const { generate, isGenerating } = useGenerateMasterPrompt(setBrief);
  const busy = isUpdating || isGenerating;
  const key = savedKey(saved);
  const savedRef = useRef(saved);
  savedRef.current = saved;

  useEffect(() => {
    const current = savedRef.current;
    setBrief(current?.brief || "");
    setFormat(matchedScriptOption(current?.format, SCRIPT_FORMATS, DEFAULT_SCRIPT_SETTINGS.format));
    setDuration(
      matchedScriptOption(
        current?.duration,
        SCRIPT_DURATIONS.map((item) => item.value),
        DEFAULT_SCRIPT_SETTINGS.duration
      )
    );
    setTone(matchedScriptOption(current?.toneBalance, SCRIPT_TONES, DEFAULT_SCRIPT_SETTINGS.tone));
    setLanguage(
      matchedScriptOption(current?.language, SCRIPT_LANGUAGES, DEFAULT_SCRIPT_SETTINGS.language)
    );
  }, [key]);

  return (
    <section className={framed ? styles.form : styles.embedded}>
      {showHeading ? <h3 className={styles.title}>Master prompt</h3> : null}
      <p className={styles.hint}>
        Fills the brief and output settings for creators that do not have their own yet.
      </p>
      <div className={styles.actions}>
        <DisabledPopover
          isDisabled={overviewMissing}
          content="Add a master brief before generating from the overview."
        >
          <Button
            disabled={busy || overviewMissing}
            onClick={() => {
              if (overviewMissing) return;
              generate({ aiWorkspaceId, type: "script-generate" });
            }}
          >
            <Wand2 size={14} />
            {isGenerating ? "Generating" : "Generate from overview"}
          </Button>
        </DisabledPopover>
      </div>
      <div
        className={
          isGenerating
            ? `${frameStyles.scriptFrameGenerating} ${styles.fieldsGenerating}`
            : undefined
        }
      >
      <div className={styles.grid}>
        <label className={styles.field}>
          <span>Brief</span>
          <Input.TextArea
            value={brief}
            rows={6}
            disabled={busy}
            placeholder="Objective, product message, creative direction, and mandatories."
            onChange={(event) => setBrief(event.target.value)}
          />
        </label>
        <div className={styles.settings}>
          <label className={styles.field}>
            <span>Format</span>
            <Select
              value={format}
              disabled={busy}
              options={SCRIPT_FORMATS.map((item) => ({ value: item, label: item }))}
              onChange={(value) => setFormat(String(value))}
            />
          </label>
          <label className={styles.field}>
            <span>Duration</span>
            <Select
              value={duration}
              disabled={busy}
              options={SCRIPT_DURATIONS.map((item) => ({ value: item.value, label: item.label }))}
              onChange={(value) => setDuration(String(value))}
            />
          </label>
          <label className={styles.field}>
            <span>Tone balance</span>
            <Select
              value={tone}
              disabled={busy}
              options={SCRIPT_TONES.map((item) => ({ value: item, label: item }))}
              onChange={(value) => setTone(String(value))}
            />
          </label>
          <label className={styles.field}>
            <span>Language</span>
            <Select
              value={language}
              disabled={busy}
              options={SCRIPT_LANGUAGES.map((item) => ({ value: item, label: item }))}
              onChange={(value) => setLanguage(String(value))}
            />
          </label>
          <div className={styles.actions}>
            <Button
              type="primary"
              disabled={!brief.trim() || busy}
              onClick={() =>
                update({
                  aiWorkspaceId,
                  brief: brief.trim(),
                  format,
                  duration,
                  toneBalance: tone,
                  language,
                })
              }
            >
              {isUpdating ? "Saving" : "Save master prompt"}
            </Button>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
}
